import { Component, Host, h, Prop, State, Method, Watch, type JSX, Event, type EventEmitter } from '@stencil/core'
import { type ISteps } from '../../utils/utils'
import { statuses, type StatusOptions } from '../../utils/statuses'
import { type IMaglrComponent } from '../../utils/IMaglrComponent'

@Component({
    tag: 'maglr-counter',
    styleUrl: 'maglr-counter.scss',
    shadow: true,
    assetsDirs: ['editorSettings'],
})

export class MaglrCounter implements IMaglrComponent {
    digits = ['-', '.', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9']
    digitsCount: number = 0
    timer: number = 0
    decimalMultiplier: number = 0
    direction: number = 1 // 1 || -1
    toSteps: []
    initValue: number = 0
    currentStep: number = 0
    currentDuration: number = 0
    initialPlay = true

    @Prop() from: number
    @Prop() steps: string = '[{"step":100,"label":"Step 1"}]'
    @Prop() duration: number = 1000
    @Prop() loop: boolean = false
    @Prop() mask: boolean = false
    @Prop() decimals: number = 0
    @Prop() separators: string = 'auto'
    @Prop() leadingzero: boolean = false
    @Prop() prefixtext: string
    @Prop() suffixtext: string
    @Prop() loopSteps: boolean = true
    @Prop() alignment: string = 'start'
    @State() count: number = 0
    @State() value: number = 0
    @State() stepState: ISteps[] = []
    @Prop() uuid: string
    @State() status: StatusOptions = statuses.BLANK
    @Prop() autostart: boolean = true

    @Event({
        eventName: 'maglr-webcomponent',
        composed: true,
        cancelable: true,
        bubbles: true,
    })
        fireEvent: EventEmitter

    @Watch('steps')
    parseMyObjectProp (newSteps: string): void {
        this.stepState = JSON.parse(newSteps)
        this.initiateStep(this.currentStep)
    }

    @Method() async start (): Promise<void> {
        if (this.autostart || !this.initialPlay) {
            this.initiateStep(this.currentStep)
            this.status = statuses.STARTED
            this.initialPlay = false
        }
    }

    async next (): Promise<void> {
        if (this.currentStep + 1 < this.stepState.length) {
            this.currentStep++
        } else if (this.loopSteps) {
            this.currentStep = 0
        }
        this.initiateStep(this.currentStep)
    }

    async previous (): Promise<void> {
        if (this.currentStep - 1 >= 0) {
            this.currentStep -= 1
        } else if (this.loopSteps) {
            this.currentStep = this.stepState.length - 1
        }
        this.initiateStep(this.currentStep)
    }

    @Method() async pause (): Promise<void> {
        this.status = statuses.PAUSED
    }

    @Method() async stop (): Promise<void> {
        this.value = this.from
        this.currentStep = 0
        this.status = statuses.STOPPED
    }

    @Method() async seek (value = 0): Promise<void> {
        this.from = this.value
        this.value = this.initValue + (this.stepState[this.currentStep].step - this.initValue) * value
    }

    @Method() async restart (): Promise<void> {
        this.currentDuration = 0
        await this.stop()

        setTimeout(() => {
            this.currentDuration = this.duration
            void this.start()
        }, 100)
    }

    @Method() async goToScreenshotState (): Promise<void> {
        this.currentDuration = 0
        this.initiateStep(0)
        void this.start()

        // setTimeout(() => {
        //     this.currentDuration = this.duration
        // }, 100)
    }

    async goToId (id: string): Promise<void> {
        const step = this.stepState.findIndex(item => item.id === id)
        if (step !== undefined) {
            this.currentStep = step
            this.initiateStep(this.currentStep)
        }
    }

    @Method() async doAction (action: string): Promise<void> {
        switch (action) {
            case 'start':
            case 'play':
                void this.start()
                break
            case 'restart':
                void this.restart()
                break
            case 'stop':
                void this.stop()
                break
            case 'pause':
                void this.pause()
                break
            case 'next':
                void this.next()
                break
            case 'previous':
                void this.previous()
                break
            default:
                void this.goToId(action)
        }
    }

    componentWillLoad (): void {
        this.value = this.from
        this.initValue = this.from
        this.currentDuration = this.duration
        this.stepState = JSON.parse(this.steps)
        // this.initiateStep(0)
        this.status = statuses.LOADED
    }

    initiateStep (step: number): void {
        this.value = this.stepState[step].step
    }

    @Watch('status')
    watchPropHandler (status: number): void {
        this.fireEvent.emit({
            uuid: this.uuid,
            type: 'status',
            status,
        })
    }

    render (): JSX.Element {
        const props = {
            from: this.from,
            value: this.value,
            duration: this.currentDuration,
            loop: this.loop,
            mask: this.currentDuration > 0 ? this.mask : false,
            decimals: this.decimals,
            separators: this.separators,
            leadingzero: this.leadingzero,
            prefixtext: this.prefixtext,
            suffixtext: this.suffixtext,
            loopSteps: this.loopSteps,
            multi: this.stepState.length > 1,
            maxdigits: Math.max(this.from.toFixed(0).length, ...this.stepState.map(entry => entry.step.toFixed(0).length)),
        }
        return (
            <Host>
                <counter-component {...props} to={this.value} class={this.alignment}></counter-component>
            </Host>
        )
    }
}
